// src/components/App/Exposures/GicsTreeMap.js
import React, { useState, useEffect } from 'react';
import ReactEcharts from 'echarts-for-react';
import { getGicsTreeMapOption } from '../charts';
import './GicsTreeMap.css';

function LevelNav({ currentLevel, onSelect }) {
  const LEVELS = ['sector', 'ig', 'industry', 'subIndustry'];
  const LABELS = {
    sector: 'Sector',
    ig: 'Industry Group',
    industry: 'Industry',
    subIndustry: 'Sub-Industry',
  };

  return (
    <div
      role="tablist"
      aria-label="GICS level navigation"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 0,
        margin: '0', // ← no top margin; spacing is from fund list padding
      }}
    >
      {LEVELS.map((lv, i) => {
        const isCurrent = lv === currentLevel;
        const isFirst = i === 0;
        const isLast = i === LEVELS.length - 1;
        return (
          <button
            key={lv}
            type="button"
            onClick={() => onSelect(lv)}
            aria-current={isCurrent ? 'step' : undefined}
            title={LABELS[lv]}
            style={{
              padding: '6px 10px',
              margin: 0,
              minWidth: 120,
              textAlign: 'center',
              fontWeight: isCurrent ? 500 : 400,
              background: isCurrent ? '#050139' : '#fff',
              color: isCurrent ? '#fff' : '#050139',
              cursor: 'pointer',
              border: '1px solid #050139',
              borderLeftWidth: isFirst ? 1 : 0, // no double border
              borderRightWidth: isLast ? 1 : 1,
              borderTopWidth: 1,
              borderBottomWidth: 1,
              borderRadius: 0,
            }}
          >
            {LABELS[lv]}
          </button>
        );
      })}
    </div>
  );
}

/***** Main component *****/
export default function GicsTreeMap({
  gicsData,
  theme = 'default',
  height = 400,
  className,
  style,
}) {
  // Is gicsData an array of funds?
  const looksLikeFunds =
    Array.isArray(gicsData) &&
    gicsData.length > 0 &&
    ('gicsExposureTree' in (gicsData[0] || {}) ||
      'id' in (gicsData[0] || {}) ||
      'ticker' in (gicsData[0] || {}));

  /***** STATE *****/
  const [selectedFundIndex, setSelectedFundIndex] = useState(0);
  const [level, setLevel] = useState('sector'); // sector -> ig -> industry -> subIndustry

  // Reset drill when fund changes
  useEffect(() => {
    setLevel('sector');
  }, [selectedFundIndex]);

  /***** OPTION (level-wide drill; pass fund label to charts.js) *****/
  const selected = looksLikeFunds ? gicsData[selectedFundIndex] : null;
  const fundLabel = looksLikeFunds
    ? `${selected?.ticker || selected?.id || ''} — ${
        selected?.name || 'Unnamed Fund'
      }`
    : '';

  const option = getGicsTreeMapOption(
    gicsData,
    looksLikeFunds ? selectedFundIndex : undefined,
    level,
    { titleLabel: fundLabel } // ← this makes the ECharts title update when you change funds
  );

  /***** HANDLERS *****/
  const handleTreemapClick = () => {
    if (level === 'sector') setLevel('ig');
    else if (level === 'ig') setLevel('industry');
    else if (level === 'industry') setLevel('subIndustry');
  };

  const goToLevel = (nextLevel) => {
    setLevel(nextLevel); // jump to any level via buttons
  };

  /***** RENDER *****/
  return (
    <div className={className} style={style}>
      {/* <div className="chartHeader">{i18n.t('gics_tree')}</div> */}

      {looksLikeFunds && (
        <div style={{ padding: '20px 20px 40px 20px' }}>
          <div
            role="radiogroup"
            aria-label="Select fund"
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: 10,
              flexWrap: 'wrap', // multiple rows if needed
            }}
          >
            {(gicsData || []).map((f, idx) => {
              const isActive = idx === selectedFundIndex;
              const ticker = f?.ticker || f?.id || `fund-${idx}`;
              const name = f?.name || 'Unnamed Fund';
              const label = `${ticker} - ${name}`;

              return (
                <button
                  key={`fund-dot-${idx}`}
                  role="radio"
                  aria-checked={isActive}
                  title={label}
                  aria-label={label}
                  onClick={() => setSelectedFundIndex(idx)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ')
                      setSelectedFundIndex(idx);
                  }}
                  style={{
                    minWidth: 240,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      border: '2px solid #050139',
                      background: isActive ? '#050139' : '#fff',
                      display: 'inline-block',
                      flex: '0 0 14px',
                    }}
                  />
                  <span
                    style={{
                      fontSize: 14,
                      lineHeight: '16px',
                      color: isActive ? '#050139' : '#5B6770',
                      fontWeight: isActive ? 600 : 400,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <LevelNav currentLevel={level} onSelect={goToLevel} />

      {/* -------- Selected fund name (centered) -------- */}
      {/* {looksLikeFunds && (
        <div
          style={{
            fontSize: 14,
            color: '#050139',
            fontWeight: 400,
            marginTop: '40px',

            textAlign: 'center',
          }}
        >
          {gicsData[selectedFundIndex]?.ticker ||
            gicsData[selectedFundIndex]?.id ||
            ''}
          {' — '}
          {gicsData[selectedFundIndex]?.name || 'Unnamed Fund'}
        </div>
      )} */}

      {/* -------- Level buttons: BELOW name, ABOVE treemap -------- */}

      {/* -------- Treemap -------- */}
      <div className="chartContainer">
        <ReactEcharts
          key={`fund-${selectedFundIndex}-lvl-${level}`}
          option={option}
          notMerge
          theme={theme}
          onEvents={{ click: handleTreemapClick }} // advance level for ALL sectors
          style={{ height, width: '100%' }}
        />
      </div>
    </div>
  );
}
